import { Component, OnDestroy, OnInit, ViewEncapsulation } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { FuseSidebarService } from "@fuse/components/sidebar/sidebar.service";
import { FuseConfigService } from "@fuse/services/config.service";
import { TranslateService } from "@ngx-translate/core";
import { NotificationsService } from "angular2-notifications";
import { IUser } from "app/interfaces/i-user";
import { navigation } from "app/navigation/navigation";
import { ApiUserService } from "app/services/api/api-user.service";
import { AuthService } from "app/services/auth/auth.service";
import { LocalStorageService } from "app/services/local-storage/local-storage.service";
import {
    ThemePreference,
    ThemeService,
} from "app/services/theme/theme.service";
import { environment } from "environments/environment";
import * as _ from "lodash";
import { Subject } from "rxjs";
import { filter, mergeMap, takeUntil } from "rxjs/operators";
import { ErrorMessageTranslatorService } from "../../../ErrorMessageTranslatorService";
import { DialogChangePassword } from "./dialog-component/change-password/dialog-change-password.component";
import { DialogEditPrivacyPolicyComponent } from "./dialog-component/privacy/edit-privacy.component";
import { DialogProfileComponent } from "./dialog-component/profile/profile.component";

@Component({
    standalone: false,
    selector: "navbar",
    templateUrl: "./navbar.component.html",
    styleUrls: ["./navbar.component.scss"],
    encapsulation: ViewEncapsulation.None,
})
export class NavbarComponent implements OnInit, OnDestroy {
    horizontalNavbar: boolean;
    rightNavbar: boolean;
    hiddenNavbar: boolean;
    languages: any;
    navigation: any;
    selectedLanguage: any;
    userStatusOptions: any[];

    public showExtraMenu: boolean;

    public userLogged: boolean;
    public titleNavBar: string;
    public themePreference: ThemePreference;

    // Private
    private _unsubscribeAll: Subject<any>;
    public user: IUser;
    public navbarColor: string = environment["navbar_color"];

    /**
     * Constructor
     *
     * @param {FuseConfigService} _fuseConfigService
     * @param {FuseSidebarService} _fuseSidebarService
     * @param {TranslateService} _translateService
     */
    constructor(
        private storage: LocalStorageService,
        public dialog: MatDialog,
        private router: Router,
        private _fuseConfigService: FuseConfigService,
        private _fuseSidebarService: FuseSidebarService,
        private _translateService: TranslateService,
        private authService: AuthService,
        private toast: NotificationsService,
        private apiUserService: ApiUserService,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private errorMessageTranslatorService: ErrorMessageTranslatorService,
        private themeService: ThemeService,
    ) {
        this.titleNavBar = environment.APP_TITLE;
        this.showExtraMenu = environment.showExtraMenu;
        this.themePreference = this.themeService.getPreference();

        this.matIconRegistry.addSvgIcon(
            `icon_logo`,
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                "assets/images/logos/logo_vettoriale.svg",
            ),
        );

        this.navigation = navigation;
        this._unsubscribeAll = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.user = this.storage.getItem("user");
        // this.tenantSelected = this.storage.getItem("tenantSelected");
        // Subscribe to the config changes
        this._fuseConfigService.config
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((settings) => {
                // this.horizontalNavbar = settings.layout.navbar.position === 'top';
                // this.rightNavbar = settings.layout.navbar.position === 'right';
                // this.hiddenNavbar = settings.layout.navbar.hidden === true;
            });

        // Set the selected language from default languages
        this.selectedLanguage = _.find(this.languages, {
            id: this._translateService.currentLang,
        });
        this.themeService
            .change()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((preference) => {
                this.themePreference = preference;
            });
        this.storage.getChangesOf<any>("data").subscribe((data) => {
            this.user = data.user;
        });
        this.authService.change().subscribe((data) => {
            this.user = this.storage.getItem("user");
            if (!!data) {
                this.userLogged = true;
            } else {
                this.userLogged = false;
            }
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        this.authService.change().unsubscribe();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key): void {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
    }

    useCondition(): void {
        this.router.navigate(["condition"]);
    }

    public get isDarkTheme(): boolean {
        if (this.themePreference === "dark") {
            return true;
        }
        if (this.themePreference === "light") {
            return false;
        }
        return window.matchMedia("(prefers-color-scheme: dark)").matches;
    }

    setTheme(preference: ThemePreference): void {
        this.themeService.setPreference(preference);
    }

    toggleTheme(): void {
        this.setTheme(this.isDarkTheme ? "light" : "dark");
    }

    edit_profile(): void {
        this.user = this.storage.getItem("user");
        this.dialog
            .open(DialogProfileComponent, {
                hasBackdrop: true,
                data: {
                    modalData: this.user,
                },
            })
            .afterClosed()
            .pipe(
                filter((result) => !!result),
                mergeMap((result) => {
                    this.user.userdata = result;
                    return this.apiUserService.apiChangeProfile(this.user);
                }),
            )
            .subscribe({
                next: (user) => {
                    this.storage.setItem("user", user);
                    this.toast.success(
                        "Aggiornamento Profilo",
                        "Profilo modificato con successo",
                    );
                },
                error: (err) => {
                    const errorMessage = _.get(err, "error.message", "");
                    const errorMessageTranslated =
                        this.errorMessageTranslatorService.translate(
                            errorMessage,
                        );
                    this.toast.error(
                        "Aggiornamento Profilo",
                        errorMessageTranslated,
                    );
                },
            });
    }

    change_password(): void {
        this.dialog.open(DialogChangePassword, {
            width: "45%",
            data: {
                modalData: this.user,
            },
        });
    }

    logout(): void {
        const externalUrl = environment.return_url;
        this.authService.logout().subscribe(() => {
            // this.router.navigate([externalUrl]);
            window.open(externalUrl, "_self");
        });
    }

    cookie(): void {
        this.router.navigate(["cookie"]);
    }

    privacy(): void {
        this.dialog
            .open(DialogEditPrivacyPolicyComponent, {
                hasBackdrop: true,
                data: {
                    modalData: this.user.userdata,
                },
            })
            .afterClosed()
            .pipe(
                filter((result) => !!result),
                mergeMap((result) => {
                    this.user.userdata = result;
                    return this.apiUserService.apiChangeProfile(this.user);
                }),
            )
            .subscribe({
                next: (user) => {
                    this.storage.setItem("user", user);
                    this.toast.success(
                        "Aggiornamento Privacy",
                        "Configurazione privacy aggiornata con successo",
                    );
                },
                error: (err) => {
                    const errorMessage = _.get(err, "error.message", "");
                    const errorMessageTranslated =
                        this.errorMessageTranslatorService.translate(
                            errorMessage,
                        );
                    this.toast.error(
                        "Aggiornamento Privacy",
                        errorMessageTranslated,
                    );
                },
            });
    }
}
